/**
 * useTaskData Hook
 *
 * Manages task/milestone data state for the spreadsheet view
 */

import { useState, useCallback } from 'react';
import { TaskDataState, TaskDataItem, NewTaskRow, NewTaskRowsState } from '../types';

export interface UseTaskDataReturn {
    taskData: TaskDataState;
    setTaskData: React.Dispatch<React.SetStateAction<TaskDataState>>;
    newTaskRows: NewTaskRowsState;
    setNewTaskRows: React.Dispatch<React.SetStateAction<NewTaskRowsState>>;

    // Task Data Operations
    updateTask: (key: string, field: keyof TaskDataItem, value: any) => void;
    getTaskState: (objectiveId: string, taskId: string) => TaskDataItem | undefined;
    resetTaskData: () => void;

    // New Task Row Operations
    addNewTaskRow: (objId: string) => void;
    removeNewTaskRow: (objId: string, rowId: string) => void;
    updateNewTaskRow: (objId: string, rowId: string, field: keyof NewTaskRow, value: any) => void;
    clearNewTaskRows: (objId: string) => void;

    // Selected Node (for Atomic Map)
    selectedNode: any;
    setSelectedNode: React.Dispatch<React.SetStateAction<any>>;
}

export const useTaskData = (): UseTaskDataReturn => {
    const [taskData, setTaskData] = useState<TaskDataState>({});
    const [newTaskRows, setNewTaskRows] = useState<NewTaskRowsState>({});
    const [selectedNode, setSelectedNode] = useState<any>(null);

    const updateTask = useCallback((key: string, field: keyof TaskDataItem, value: any) => {
        setTaskData(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value
            }
        }));
    }, []);

    const getTaskState = useCallback((objectiveId: string, taskId: string): TaskDataItem | undefined => {
        const key = `${objectiveId}-${taskId}`;
        return taskData[key];
    }, [taskData]);

    const resetTaskData = useCallback(() => {
        setTaskData({});
    }, []);

    const addNewTaskRow = useCallback((objId: string) => {
        const newRow: NewTaskRow = {
            id: `new-${Date.now()}`,
            title: '',
            assignedTo: '',
            startDate: '',
            dueDate: '',
            status: 'not_started'
        };
        setNewTaskRows(prev => ({
            ...prev,
            [objId]: [...(prev[objId] || []), newRow]
        }));
    }, []);

    const removeNewTaskRow = useCallback((objId: string, rowId: string) => {
        setNewTaskRows(prev => ({
            ...prev,
            [objId]: (prev[objId] || []).filter(row => row.id !== rowId)
        }));
    }, []);

    const updateNewTaskRow = useCallback((
        objId: string,
        rowId: string,
        field: keyof NewTaskRow,
        value: any
    ) => {
        setNewTaskRows(prev => ({
            ...prev,
            [objId]: (prev[objId] || []).map(row =>
                row.id === rowId ? { ...row, [field]: value } : row
            )
        }));
    }, []);

    const clearNewTaskRows = useCallback((objId: string) => {
        setNewTaskRows(prev => ({
            ...prev,
            [objId]: []
        }));
    }, []);

    return {
        taskData,
        setTaskData,
        newTaskRows,
        setNewTaskRows,

        // Task Data Operations
        updateTask,
        getTaskState,
        resetTaskData,

        // New Task Row Operations
        addNewTaskRow,
        removeNewTaskRow,
        updateNewTaskRow,
        clearNewTaskRows,

        // Selected Node
        selectedNode,
        setSelectedNode,
    };
};

export default useTaskData;
